import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Trash2, Camera, Loader2, Image as ImageIcon, ChevronsUpDown, ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useInventoryItems } from "@/hooks/useInventory";
import { useInventoryRooms } from "@/hooks/useInventoryRooms";
import type { GuestAmenityStagingLine, GuestAmenityStagingSubitem } from "@/types/checklist";
import {
  effectiveLeaveOutRule,
  isGuestAmenityKitLine,
  newGuestAmenityKitLine,
  newGuestAmenityPlacement,
  newGuestAmenityStagingLine,
  newGuestAmenityStagingSubitem,
} from "@/lib/guestAmenityStaging";
import {
  computeLeaveOutQty,
  formatLeaveOutPreview,
  formatLeaveOutRuleSummary,
  formatWhereLine,
  type LeaveOutMode,
  type LeaveOutRule,
} from "@/lib/leaveOut";
import { toast } from "@/hooks/use-toast";

export interface GuestAmenityStagingEditorProps {
  propertyId: string;
  checklistId: string;
  lines: GuestAmenityStagingLine[];
  onLinesChange: (next: GuestAmenityStagingLine[]) => void;
}

export default function GuestAmenityStagingEditor({
  propertyId,
  checklistId,
  lines,
  onLinesChange,
}: GuestAmenityStagingEditorProps) {
  const { data: invRooms = [] } = useInventoryRooms(propertyId);
  const { data: invItems = [] } = useInventoryItems(propertyId);
  const [uploadingLineId, setUploadingLineId] = useState<string | null>(null);
  const [itemPopoverLineId, setItemPopoverLineId] = useState<string | null>(null);
  const [itemSearchByLine, setItemSearchByLine] = useState<Record<string, string>>({});
  /** When true, that line's body is collapsed (header stays visible). */
  const [lineCollapsed, setLineCollapsed] = useState<Record<string, boolean>>({});

  const roomNameById = useMemo(() => {
    const m = new Map<string, string>();
    invRooms.forEach((r) => m.set(r.id, r.name));
    return m;
  }, [invRooms]);

  const patchLine = (id: string, patch: Partial<GuestAmenityStagingLine>) => {
    onLinesChange(lines.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const patchSubitem = (lineId: string, subId: string, patch: Partial<GuestAmenityStagingSubitem>) => {
    onLinesChange(
      lines.map((l) => {
        if (l.id !== lineId) return l;
        const subs = [...(l.subitems ?? [])];
        const i = subs.findIndex((s) => s.id === subId);
        if (i < 0) return l;
        subs[i] = { ...subs[i], ...patch };
        return { ...l, subitems: subs };
      })
    );
  };

  const setLeaveOutRule = (lineId: string, next: LeaveOutRule) => {
    onLinesChange(
      lines.map((l) => {
        if (l.id !== lineId) return l;
        return {
          ...l,
          leave_out_rule: next,
          target_count: Math.max(0, Math.floor(next.qty)),
        };
      }),
    );
  };

  const setLeaveOutMode = (lineId: string, mode: LeaveOutMode) => {
    const line = lines.find((l) => l.id === lineId);
    if (!line) return;
    const prev = effectiveLeaveOutRule(line);
    if (mode === "fixed") {
      setLeaveOutRule(lineId, {
        mode: "fixed",
        qty: prev.qty || 1,
        per_guests: prev.per_guests ?? 2,
        min_qty: null,
        max_qty: null,
      });
      return;
    }
    setLeaveOutRule(lineId, {
      mode: "per_guests",
      qty: prev.qty || 1,
      per_guests: prev.per_guests && prev.per_guests >= 1 ? prev.per_guests : 2,
      min_qty: prev.min_qty ?? null,
      max_qty: prev.max_qty ?? null,
    });
  };

  const patchPlacement = (
    lineId: string,
    placeId: string,
    patch: Partial<{ place_label: string; qty: number; property_room_id: string | null }>,
  ) => {
    onLinesChange(
      lines.map((l) => {
        if (l.id !== lineId) return l;
        const placements = [...(l.placements ?? [])];
        const i = placements.findIndex((p) => p.id === placeId);
        if (i < 0) return l;
        placements[i] = { ...placements[i]!, ...patch };
        return { ...l, placements };
      }),
    );
  };

  const addPlacement = (lineId: string) => {
    onLinesChange(
      lines.map((l) =>
        l.id === lineId
          ? { ...l, placements: [...(l.placements ?? []), newGuestAmenityPlacement("", 1)] }
          : l,
      ),
    );
  };

  const removePlacement = (lineId: string, placeId: string) => {
    onLinesChange(
      lines.map((l) => {
        if (l.id !== lineId) return l;
        return { ...l, placements: (l.placements ?? []).filter((p) => p.id !== placeId) };
      }),
    );
  };

  const addLine = () => onLinesChange([...lines, newGuestAmenityStagingLine()]);
  const addKitLine = () => onLinesChange([...lines, newGuestAmenityKitLine()]);
  const removeLine = (id: string) => onLinesChange(lines.filter((l) => l.id !== id));

  const setKitMode = (lineId: string) => {
    onLinesChange(
      lines.map((l) => {
        if (l.id !== lineId) return l;
        return {
          ...l,
          target_count: l.target_count < 1 ? 1 : l.target_count,
          subitems: (l.subitems?.length ?? 0) > 0 ? l.subitems : [newGuestAmenityStagingSubitem()],
        };
      })
    );
  };

  const setSingleMode = (lineId: string) => {
    onLinesChange(
      lines.map((l) => (l.id === lineId ? { ...l, subitems: undefined } : l))
    );
  };

  const addSubitem = (lineId: string) => {
    onLinesChange(
      lines.map((l) =>
        l.id === lineId ? { ...l, subitems: [...(l.subitems ?? []), newGuestAmenityStagingSubitem()] } : l
      )
    );
  };

  const removeSubitem = (lineId: string, subId: string) => {
    onLinesChange(
      lines.map((l) => {
        if (l.id !== lineId) return l;
        const next = (l.subitems ?? []).filter((s) => s.id !== subId);
        return { ...l, subitems: next.length ? next : undefined };
      })
    );
  };

  const uploadHostPhoto = async (lineId: string, file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please select an image file", variant: "destructive" });
      return;
    }
    const ext = file.name.split(".").pop() || "jpg";
    const safeChecklist = checklistId.replace(/[^a-zA-Z0-9_-]/g, "_");
    const safeLine = lineId.replace(/[^a-zA-Z0-9_-]/g, "_");
    const filePath = `guest-staging-ref/${propertyId}/${safeChecklist}/${safeLine}/${Date.now()}.${ext}`;
    setUploadingLineId(lineId);
    try {
      const { error: uploadError } = await supabase.storage.from("reference-photos").upload(filePath, file, {
        upsert: false,
      });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from("reference-photos").getPublicUrl(filePath);
      patchLine(lineId, { host_reference_photo_url: urlData.publicUrl });
      toast({ title: "Photo added", description: "Cleaners will see this as a staging reference." });
    } catch (e) {
      console.error(e);
      toast({ title: "Upload failed", variant: "destructive" });
    } finally {
      setUploadingLineId(null);
    }
  };

  const removeHostPhoto = async (line: GuestAmenityStagingLine) => {
    const url = line.host_reference_photo_url;
    patchLine(line.id, { host_reference_photo_url: null });
    if (!url) return;
    try {
      const urlParts = url.split("/reference-photos/");
      if (urlParts[1]) {
        await supabase.storage.from("reference-photos").remove([decodeURIComponent(urlParts[1])]);
      }
    } catch {
      /* ignore */
    }
  };

  const collapseAllLines = () => {
    const next: Record<string, boolean> = {};
    lines.forEach((l) => {
      next[l.id] = true;
    });
    setLineCollapsed(next);
  };

  const expandAllLines = () => setLineCollapsed({});

  return (
    <div className="space-y-4">
      {lines.length >= 2 ? (
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={collapseAllLines}>
            Collapse all
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={expandAllLines}>
            Expand all
          </Button>
        </div>
      ) : null}
      {lines.map((line, idx) => {
        const q = (itemSearchByLine[line.id] ?? "").trim().toLowerCase();
        const filteredItems =
          q.length === 0
            ? invItems.slice(0, 50)
            : invItems.filter((it) => (it.item_name || "").toLowerCase().includes(q)).slice(0, 50);

        const collapsed = Boolean(lineCollapsed[line.id]);
        const isKit = isGuestAmenityKitLine(line);
        const partCount = (line.subitems ?? []).length;
        const rule = effectiveLeaveOutRule(line);
        const previewGuests = 4;
        const previewLeave = computeLeaveOutQty(previewGuests, rule).leave_qty;
        const previewText = formatLeaveOutPreview(previewGuests, rule);
        const ruleSummary = formatLeaveOutRuleSummary(rule);
        const wherePreview = formatWhereLine(
          previewLeave,
          line.placements,
          line.room_name_snapshot ??
            (line.property_room_id ? roomNameById.get(line.property_room_id) : null),
        );
        const summaryLabel = line.item_label?.trim() || (isKit ? "Gift basket / kit" : "Untitled item");
        const summarySuffix =
          isKit && partCount > 0
            ? ` · ${partCount} part${partCount !== 1 ? "s" : ""} · ${ruleSummary}`
            : ` · ${ruleSummary}`;

        return (
          <Card key={line.id} className="border-border/80 overflow-hidden">
            <Collapsible
              open={!collapsed}
              onOpenChange={(open) => setLineCollapsed((prev) => ({ ...prev, [line.id]: !open }))}
            >
              <CardHeader className="space-y-0 border-b border-border/70 p-0">
                <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 px-3 py-3 sm:gap-3 sm:px-6 sm:py-3.5">
                  <CollapsibleTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-10 w-10 shrink-0 self-center text-muted-foreground hover:text-foreground"
                      aria-expanded={!collapsed}
                      aria-label={collapsed ? "Expand item" : "Collapse item"}
                    >
                      <ChevronDown
                        className={cn("h-4 w-4 transition-transform duration-200", collapsed && "-rotate-90")}
                      />
                    </Button>
                  </CollapsibleTrigger>
                  <CardTitle className="text-base font-semibold leading-snug text-center min-w-0 m-0 tracking-normal">
                    <span className="inline-flex w-full flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5">
                      <span className="text-muted-foreground font-medium">Item {idx + 1}</span>
                      {collapsed ? (
                        <>
                          <span className="text-muted-foreground">·</span>
                          <span className="font-semibold text-foreground break-words">
                            {summaryLabel}
                            {summarySuffix ? (
                              <span className="font-medium text-muted-foreground">{summarySuffix}</span>
                            ) : null}
                          </span>
                        </>
                      ) : null}
                    </span>
                  </CardTitle>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-10 shrink-0 justify-self-end gap-1.5 px-2 text-destructive hover:text-destructive sm:px-3"
                    onClick={() => removeLine(line.id)}
                  >
                    <Trash2 className="h-4 w-4 shrink-0" />
                    <span className="text-sm font-medium">Remove</span>
                  </Button>
                </div>
              </CardHeader>
              <CollapsibleContent>
            <CardContent className="space-y-4 px-3 pb-6 pt-2 sm:px-6">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">Item type</Label>
                <div className="grid grid-cols-2 gap-2 p-1 rounded-lg border bg-muted/30">
                  <Button
                    type="button"
                    size="sm"
                    variant={isKit ? "ghost" : "secondary"}
                    className={cn("h-9", !isKit && "shadow-sm")}
                    onClick={() => setSingleMode(line.id)}
                  >
                    Single item
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={isKit ? "secondary" : "ghost"}
                    className={cn("h-9", isKit && "shadow-sm")}
                    onClick={() => setKitMode(line.id)}
                  >
                    Kit / gift basket
                  </Button>
                </div>
                {isKit ? (
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    Name the kit, then list what goes inside (e.g. sparkling water, snacks, welcome card). Cleaners
                    see one kit with all parts listed.
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    One product with a quantity (e.g. 4 toilet paper rolls). Switch to kit mode for baskets with
                    multiple different items inside.
                  </p>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Room</Label>
                  <Select
                    value={line.property_room_id ?? "none"}
                    onValueChange={(v) => {
                      const id = v === "none" ? null : v;
                      patchLine(line.id, {
                        property_room_id: id,
                        room_name_snapshot: id ? roomNameById.get(id) ?? null : null,
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select room" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unspecified</SelectItem>
                      {invRooms.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{isKit ? "Kit name" : "Item"}</Label>
                  <Popover
                    open={itemPopoverLineId === line.id}
                    onOpenChange={(o) => {
                      setItemPopoverLineId(o ? line.id : null);
                      if (o) setItemSearchByLine((s) => ({ ...s, [line.id]: "" }));
                    }}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        className={cn("w-full justify-between font-normal", !line.item_label && "text-muted-foreground")}
                      >
                        {line.item_label
                          ? line.item_label
                          : isKit
                            ? "e.g. Welcome gift basket…"
                            : "Search inventory or type a custom item…"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder="Search property inventory…"
                          value={itemSearchByLine[line.id] ?? ""}
                          onValueChange={(v) => setItemSearchByLine((s) => ({ ...s, [line.id]: v }))}
                        />
                        <CommandList>
                          <CommandEmpty>No inventory matches. Type a name and use &quot;Use custom item&quot;.</CommandEmpty>
                          <CommandGroup heading="Inventory on this property">
                            {filteredItems.map((it) => (
                              <CommandItem
                                key={it.id}
                                value={it.id}
                                onSelect={() => {
                                  patchLine(line.id, {
                                    inventory_item_id: it.id,
                                    item_label: it.item_name,
                                  });
                                  setItemPopoverLineId(null);
                                }}
                              >
                                <span className="truncate">{it.item_name}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                          <CommandItem
                            value="__custom__"
                            onSelect={() => {
                              const name = (itemSearchByLine[line.id] ?? "").trim();
                              if (!name) {
                                toast({
                                  title: "Enter a name",
                                  description: "Type the custom item name in the search box first.",
                                  variant: "destructive",
                                });
                                return;
                              }
                              patchLine(line.id, { inventory_item_id: null, item_label: name });
                              setItemPopoverLineId(null);
                            }}
                          >
                            Use custom item (not saved to Inventory tab)
                          </CommandItem>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-[11px] text-muted-foreground">
                    Picking inventory only links this checklist to that name; it does not create or edit inventory rows.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={line.description}
                  onChange={(e) => patchLine(line.id, { description: e.target.value })}
                  rows={3}
                  placeholder={
                    isKit
                      ? "e.g. Place one welcome basket on the kitchen counter before guests arrive."
                      : "e.g. Leave 4 toilet paper rolls in the main bathroom cabinet."
                  }
                />
              </div>

              {isKit ? (
                <div className="space-y-3 rounded-lg border border-primary/25 bg-primary/5 p-3 sm:p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <Label className="text-sm font-semibold">What&apos;s inside each kit</Label>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Quantities are per kit (e.g. 2 waters + 1 snack bar in each basket).
                      </p>
                    </div>
                    <Button type="button" size="sm" variant="secondary" onClick={() => addSubitem(line.id)}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add part
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {(line.subitems ?? []).map((sub, subIdx) => (
                      <div
                        key={sub.id}
                        className="grid gap-2 sm:grid-cols-12 sm:items-end items-stretch border rounded-md p-2 sm:p-3 bg-background"
                      >
                        <div className="sm:col-span-1 flex items-center sm:items-end pb-0 sm:pb-2">
                          <span className="text-xs font-medium text-muted-foreground tabular-nums">{subIdx + 1}.</span>
                        </div>
                        <div className="sm:col-span-4 space-y-1">
                          <Label className="text-xs">Part name</Label>
                          <Input
                            value={sub.item_label}
                            onChange={(e) => patchSubitem(line.id, sub.id, { item_label: e.target.value })}
                            placeholder="e.g. Sparkling water"
                          />
                        </div>
                        <div className="sm:col-span-3 space-y-1">
                          <Label className="text-xs">Qty per kit</Label>
                          <Input
                            type="number"
                            min={0}
                            value={sub.target_count === 0 ? "" : sub.target_count}
                            onChange={(e) =>
                              patchSubitem(line.id, sub.id, {
                                target_count:
                                  e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0),
                              })
                            }
                          />
                        </div>
                        <div className="sm:col-span-3 space-y-1">
                          <Label className="text-xs">Note (optional)</Label>
                          <Input
                            value={sub.description ?? ""}
                            onChange={(e) => patchSubitem(line.id, sub.id, { description: e.target.value })}
                            placeholder="e.g. Brand from closet"
                          />
                        </div>
                        <div className="sm:col-span-1 flex justify-end pb-0 sm:pb-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeSubitem(line.id, sub.id)}
                            aria-label="Remove part"
                            disabled={(line.subitems ?? []).length <= 1}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-3 sm:col-span-2">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                    Leave-out mode
                  </Label>
                  <div className="grid grid-cols-2 gap-2 p-1 rounded-xl border bg-muted/30 max-w-sm">
                    <Button
                      type="button"
                      size="sm"
                      variant={rule.mode === "fixed" ? "secondary" : "ghost"}
                      className={cn("h-11", rule.mode === "fixed" && "shadow-sm")}
                      onClick={() => setLeaveOutMode(line.id, "fixed")}
                    >
                      Fixed
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={rule.mode === "per_guests" ? "secondary" : "ghost"}
                      className={cn("h-11", rule.mode === "per_guests" && "shadow-sm")}
                      onClick={() => setLeaveOutMode(line.id, "per_guests")}
                    >
                      Per guests
                    </Button>
                  </div>

                  {rule.mode === "fixed" ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <Label className="text-sm font-semibold shrink-0">
                        {isKit ? "How many kits to place" : "Leave out"}
                      </Label>
                      <Input
                        type="number"
                        min={0}
                        className="w-24 h-11"
                        value={rule.qty === 0 ? "" : rule.qty}
                        onChange={(e) =>
                          setLeaveOutRule(line.id, {
                            ...rule,
                            mode: "fixed",
                            qty: e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0),
                          })
                        }
                      />
                      <span className="rounded-full bg-muted px-3 py-1.5 text-sm font-semibold text-muted-foreground">
                        {previewText}
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <span className="text-sm font-semibold">Leave</span>
                        <Input
                          type="number"
                          min={0}
                          className="w-20 h-11"
                          value={rule.qty === 0 ? "" : rule.qty}
                          onChange={(e) =>
                            setLeaveOutRule(line.id, {
                              ...rule,
                              mode: "per_guests",
                              qty: e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0),
                            })
                          }
                        />
                        <span className="text-sm font-semibold">every</span>
                        <Input
                          type="number"
                          min={1}
                          className="w-20 h-11"
                          value={rule.per_guests ?? 2}
                          onChange={(e) =>
                            setLeaveOutRule(line.id, {
                              ...rule,
                              mode: "per_guests",
                              per_guests: Math.max(1, parseInt(e.target.value, 10) || 1),
                            })
                          }
                        />
                        <span className="text-sm font-semibold">guests</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-muted-foreground">Min</span>
                          <Input
                            type="number"
                            min={0}
                            className="w-16 h-9"
                            placeholder={String(rule.qty || 1)}
                            value={rule.min_qty ?? ""}
                            onChange={(e) =>
                              setLeaveOutRule(line.id, {
                                ...rule,
                                mode: "per_guests",
                                min_qty:
                                  e.target.value === ""
                                    ? null
                                    : Math.max(0, parseInt(e.target.value, 10) || 0),
                              })
                            }
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-muted-foreground">Max</span>
                          <Input
                            type="number"
                            min={0}
                            className="w-16 h-9"
                            placeholder="—"
                            value={rule.max_qty ?? ""}
                            onChange={(e) =>
                              setLeaveOutRule(line.id, {
                                ...rule,
                                mode: "per_guests",
                                max_qty:
                                  e.target.value === ""
                                    ? null
                                    : Math.max(0, parseInt(e.target.value, 10) || 0),
                              })
                            }
                          />
                        </div>
                      </div>
                      <div className="inline-flex rounded-xl bg-[#e2eeed] px-4 py-2.5 text-sm font-bold text-[#15776d]">
                        {previewText}
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-3 sm:col-span-2 rounded-lg border border-border/80 bg-muted/20 p-3 sm:p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <Label className="text-sm font-semibold">Where (cleaner sees this)</Label>
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                        e.g. 1 in Bathroom 1 · 1 in Bathroom 2. Amounts are weights — the app splits the leave-out
                        total across these places.
                      </p>
                    </div>
                    <Button type="button" size="sm" variant="secondary" onClick={() => addPlacement(line.id)}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add place
                    </Button>
                  </div>
                  {(line.placements ?? []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      No places yet — cleaners will see “{previewLeave} in{" "}
                      {line.room_name_snapshot ||
                        (line.property_room_id ? roomNameById.get(line.property_room_id) : null) ||
                        "the unit"}
                      ” from the Placement room above.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {(line.placements ?? []).map((p) => (
                        <div key={p.id} className="flex flex-wrap items-end gap-2">
                          <div className="space-y-1 w-20">
                            <Label className="text-xs">Qty</Label>
                            <Input
                              type="number"
                              min={0}
                              value={p.qty === 0 ? "" : p.qty}
                              onChange={(e) =>
                                patchPlacement(line.id, p.id, {
                                  qty: e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0),
                                })
                              }
                            />
                          </div>
                          <div className="space-y-1 flex-1 min-w-[140px]">
                            <Label className="text-xs">Place</Label>
                            <Input
                              value={p.place_label}
                              onChange={(e) => patchPlacement(line.id, p.id, { place_label: e.target.value })}
                              placeholder="Bathroom 1"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => removePlacement(line.id, p.id)}
                            aria-label="Remove place"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  {wherePreview ? (
                    <div className="rounded-lg border border-primary/20 bg-background px-3 py-2 text-sm">
                      <span className="text-muted-foreground">Cleaner sees: </span>
                      <span className="font-semibold">
                        Leave {previewLeave} — {wherePreview}
                      </span>
                    </div>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <Label>Host reference photo (optional)</Label>
                  <div className="flex items-start gap-3">
                    <div className="h-16 w-16 rounded-md border bg-muted flex items-center justify-center overflow-hidden shrink-0">
                      {line.host_reference_photo_url ? (
                        <img src={line.host_reference_photo_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <ImageIcon className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id={`gst-photo-${line.id}`}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = "";
                          if (f) void uploadHostPhoto(line.id, f);
                        }}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={uploadingLineId === line.id}
                        onClick={() => document.getElementById(`gst-photo-${line.id}`)?.click()}
                      >
                        {uploadingLineId === line.id ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Camera className="h-4 w-4 mr-2" />
                        )}
                        {line.host_reference_photo_url ? "Change photo" : "Add photo"}
                      </Button>
                      {line.host_reference_photo_url ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="text-destructive h-8"
                          onClick={() => void removeHostPhoto(line)}
                        >
                          Remove photo
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        );
      })}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Button
          type="button"
          variant="outline"
          className="min-h-11 flex items-center justify-center gap-2"
          onClick={addLine}
        >
          <Plus className="h-4 w-4 shrink-0" />
          Add single item
        </Button>
        <Button
          type="button"
          variant="secondary"
          className="min-h-11 flex items-center justify-center gap-2"
          onClick={addKitLine}
        >
          <Plus className="h-4 w-4 shrink-0" />
          Add gift basket / kit
        </Button>
      </div>
    </div>
  );
}
